import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Award,
  FolderOpen, Settings, ChevronRight, X, LogOut, BarChart3,
  ShoppingBag,
  History,
  BanknoteIcon,
  BookOpen,
} from 'lucide-react';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import aural_logo from '../../assets/images/aural_ico.png';

interface NavSubItem { to: string; label: string; }
interface NavItem { to: string; icon: React.ReactNode; label: string; badge?: number; children?: NavSubItem[]; }
interface NavGroup { title: string; items: NavItem[]; }

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { to: '/dashboard', icon: <LayoutDashboard size={17} />, label: 'Dashboard' },
      // { to: '/content/analytics', icon: <BarChart3 size={17} />, label: 'Analytics' },
    ],
  },
  {
    title: 'Content',
    items: [
      {
        to: '/content/products', icon: <ShoppingBag size={17} />, label: 'Products',
        children: [
          { to: '/content/products', label: 'All Products' },
          { to: '/manage/product', label: 'Manage Product' },
        ],
      },
      {
        to: '/content/certifications', icon: <Award size={17} />, label: 'Certifications',
        children: [
          { to: '/content/certifications', label: 'All Certifications' },
          { to: '/manage/certification', label: 'Manage Certification' },
        ],
      },
      {
        to: '/content/blogs', icon: <BookOpen size={17} />, label: 'Blogs',
        children: [
          { to: '/content/blogs', label: 'All Blog Posts' },
          { to: '/manage/blog', label: 'Add Blog Post' },
        ],
      },
      { to: '/quotes', icon: <History size={17} />, label: 'All Quotes' },
      { to: '/invoices', icon: <BanknoteIcon size={17} />, label: 'Invoices' },
      // {
      //   to: '/content/contact', icon: <Mail size={17} />, label: 'Contact',
      //   children: [
      //     { to: '/content/contact', label: 'All Entries' },
      //     { to: '/content/contact/add', label: 'Add New' },
      //   ],
      // },
      // {
      //   to: '/content/about', icon: <Users size={17} />, label: 'About',
      //   children: [
      //     { to: '/content/about', label: 'Overview' },
      //     { to: '/content/about/edit', label: 'Edit' },
      //   ],
      // },
    ],
  },
  // {
  //   title: 'Assets',
  //   items: [
  //     {
  //       to: '/files', icon: <FolderOpen size={17} />, label: 'File Manager',
  //       children: [
  //         { to: '/content/files', label: 'All Files' },
  //         { to: '/content/upload', label: 'Upload New' },
  //       ],
  //     },
  //     // { to: '/pages', icon: <FileText size={17} />, label: 'Pages' },
  //   ],
  // },
  // {
  //   title: 'System',
  //   items: [
  //     { to: '/settings', icon: <Settings size={17} />, label: 'Settings' },
  //   ],
  // },
];

function NavItems({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    // Auto-open the group that matches the current path
    const initial: Record<string, boolean> = {};
    NAV_GROUPS.forEach(group =>
      group.items.forEach(item => {
        if (item.children?.some(c => location.pathname.startsWith(c.to))) {
          initial[item.to] = true;
        }
      })
    );
    return initial;
  });

  const toggleGroup = (to: string) => {
    setOpenGroups(prev => ({ ...prev, [to]: !prev[to] }));
  };

  return (
    <nav style={{ flex: 1, padding: '12px 12px', overflowY: 'auto', overflowX: 'hidden' }}>
      {NAV_GROUPS.map((group) => (
        <div key={group.title} style={{ marginBottom: 4 }}>
          {!collapsed && (
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text3)',
              letterSpacing: '1px', textTransform: 'uppercase', padding: '12px 8px 6px'
            }}>
              {group.title}
            </div>
          )}
          {collapsed && <div style={{ height: 16 }} />}

          {group.items.map((item) => {
            const hasChildren = !!item.children?.length;
            const isActive = !hasChildren && (location.pathname === item.to || location.pathname.startsWith(item.to + '/'));
            const isParentActive = hasChildren && item.children!.some(c => location.pathname.startsWith(c.to));
            const isOpen = !!openGroups[item.to];

            return (
              <div key={item.to}>
                {/* Parent row */}
                {hasChildren ? (
                  <div
                    onClick={() => toggleGroup(item.to)}
                    style={{
                      display: 'flex', alignItems: 'center',
                      gap: collapsed ? 0 : 10,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      padding: '10px', borderRadius: 9, marginBottom: 2, cursor: 'pointer',
                      background: isParentActive ? 'var(--or-dim)' : 'transparent',
                      border: `1px solid ${isParentActive ? 'rgba(191,61,12,0.2)' : 'transparent'}`,
                      transition: 'all 0.15s', position: 'relative',
                    }}
                    onMouseEnter={e => { if (!isParentActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={e => { if (!isParentActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    {isParentActive && (
                      <div style={{
                        position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)',
                        width: 3, height: 20, background: 'var(--or)', borderRadius: '0 3px 3px 0'
                      }} />
                    )}
                    <span style={{ color: isParentActive ? 'var(--or)' : 'var(--text3)', flexShrink: 0, display: 'flex' }}>
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <>
                        <span style={{
                          fontSize: 14, fontWeight: isParentActive ? 500 : 400,
                          color: isParentActive ? 'var(--or)' : 'var(--text2)', flex: 1, whiteSpace: 'nowrap'
                        }}>
                          {item.label}
                        </span>
                        {item.badge !== undefined && (
                          <span style={{
                            fontSize: 11, fontWeight: 600, color: 'var(--or)',
                            background: 'var(--or-dim)', border: '1px solid rgba(191,61,12,0.2)',
                            borderRadius: 100, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                            marginRight: 4,
                          }}>
                            {item.badge}
                          </span>
                        )}
                        <ChevronRight
                          size={13}
                          style={{
                            color: 'var(--text3)', flexShrink: 0,
                            transform: isOpen ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.2s cubic-bezier(0.4,0,0.2,1)',
                          }}
                        />
                      </>
                    )}
                  </div>
                ) : (
                  <NavLink to={item.to} style={{ textDecoration: 'none' }} onClick={() => onNavigate?.()}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: collapsed ? 0 : 10,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      padding: '10px', borderRadius: 9, marginBottom: 2, cursor: 'pointer',
                      background: isActive ? 'var(--or-dim)' : 'transparent',
                      border: `1px solid ${isActive ? 'rgba(191,61,12,0.2)' : 'transparent'}`,
                      transition: 'all 0.15s', position: 'relative',
                    }}
                      onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      {isActive && (
                        <div style={{
                          position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)',
                          width: 3, height: 20, background: 'var(--or)', borderRadius: '0 3px 3px 0'
                        }} />
                      )}
                      <span style={{ color: isActive ? 'var(--or)' : 'var(--text3)', flexShrink: 0, display: 'flex' }}>
                        {item.icon}
                      </span>
                      {!collapsed && (
                        <>
                          <span style={{
                            fontSize: 14, fontWeight: isActive ? 500 : 400,
                            color: isActive ? 'var(--or)' : 'var(--text2)', flex: 1, whiteSpace: 'nowrap'
                          }}>
                            {item.label}
                          </span>
                          {item.badge !== undefined && (
                            <span style={{
                              fontSize: 11, fontWeight: 600, color: 'var(--or)',
                              background: 'var(--or-dim)', border: '1px solid rgba(191,61,12,0.2)',
                              borderRadius: 100, padding: '1px 6px', minWidth: 18, textAlign: 'center'
                            }}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </NavLink>
                )}

                {/* Sub-items */}
                {hasChildren && !collapsed && (
                  <div style={{
                    overflow: 'hidden',
                    maxHeight: isOpen ? `${item.children!.length * 36}px` : '0px',
                    transition: 'max-height 0.2s cubic-bezier(0.4,0,0.2,1)',
                  }}>
                    {item.children!.map((child) => {
                      const isChildActive = location.pathname === child.to;
                      return (
                        <NavLink key={child.to} to={child.to} style={{ textDecoration: 'none' }} onClick={() => onNavigate?.()}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '7px 10px 7px 36px',
                            borderRadius: 8, marginBottom: 1, cursor: 'pointer',
                            fontSize: 13,
                            color: isChildActive ? 'var(--or)' : 'var(--text3)',
                            background: isChildActive ? 'var(--or-dim)' : 'transparent',
                            transition: 'all 0.15s',
                          }}
                            onMouseEnter={e => { if (!isChildActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; (e.currentTarget as HTMLElement).style.color = isChildActive ? 'var(--or)' : 'var(--text2)'; }}
                            onMouseLeave={e => { if (!isChildActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = isChildActive ? 'var(--or)' : 'var(--text3)'; }}
                          >
                            <div style={{
                              width: 5, height: 5, borderRadius: '50%',
                              background: isChildActive ? 'var(--or)' : 'var(--text3)',
                              flexShrink: 0, opacity: isChildActive ? 1 : 0.5,
                            }} />
                            {child.label}
                          </div>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

// DesktopSidebar, MobileDrawer, LogoMark, UserAvatar, and default export
// remain exactly the same as your original — no changes needed there.
/* Desktop sidebar */
function DesktopSidebar() {
  const { collapsed, toggleCollapsed } = useSidebar();
  const { user, logout } = useAuth();
  const W = collapsed ? 72 : 256;

  return (
    <div style={{
      width: W, height: '100vh', background: 'var(--surface)',
      borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
      transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden', flexShrink: 0
    }}>

      {/* Logo */}
      <div style={{
        height: 'var(--topbar-h)', display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        padding: collapsed ? 0 : '0 16px', borderBottom: '1px solid var(--border)', flexShrink: 0
      }}>
        {!collapsed ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LogoMark />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.2px' }}>Aurel Global</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Admin Panel</div>
            </div>
          </div>
        ) : <LogoMark />}
      </div>

      <NavItems collapsed={collapsed} />

      {/* User + collapse */}
      <div style={{ padding: collapsed ? '12px 8px' : '12px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--card)', borderRadius: 10, padding: '10px',
          justifyContent: collapsed ? 'center' : 'flex-start'
        }}>
          <UserAvatar name={user?.name} />
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 500, color: 'var(--text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {user?.name || 'Admin'}
                </div>
                <div style={{
                  fontSize: 11, color: 'var(--text3)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {user?.email}
                </div>
              </div>
              <button onClick={logout} title="Sign out" style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text3)', padding: 4, borderRadius: 5,
                display: 'flex', transition: 'color 0.15s', flexShrink: 0
              }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--red)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}>
                <LogOut size={14} />
              </button>
            </>
          )}
        </div>

        <button onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', marginTop: 8, padding: '7px', background: 'none',
          border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer',
          color: 'var(--text3)', transition: 'all 0.15s', fontFamily: "'Inter',sans-serif"
        }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; (e.currentTarget as HTMLElement).style.color = 'var(--text)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none'; (e.currentTarget as HTMLElement).style.color = 'var(--text3)'; }}>
          <ChevronRight size={14} style={{ transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)' }} />
          {!collapsed && <span style={{ fontSize: 12, marginLeft: 6, fontWeight: 400 }}>Collapse</span>}
        </button>
      </div>
    </div>
  );
}

/* Mobile drawer */
function MobileDrawer() {
  const { mobileOpen, closeMobile } = useSidebar();
  const { user, logout } = useAuth();
  if (!mobileOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex' }}>
      <div onClick={closeMobile} style={{
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)'
      }} />
      <div style={{
        position: 'relative', zIndex: 1, width: 256, height: '100vh',
        background: 'var(--surface)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'slideRight 0.25s cubic-bezier(0.4,0,0.2,1) both'
      }}>

        {/* Header */}
        <div style={{
          height: 'var(--topbar-h)', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 16px',
          borderBottom: '1px solid var(--border)', flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LogoMark />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Aurel Global</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Admin Panel</div>
            </div>
          </div>
          <button onClick={closeMobile} style={{
            background: 'none', border: 'none',
            cursor: 'pointer', color: 'var(--text3)', padding: 4, borderRadius: 6,
            display: 'flex', alignItems: 'center'
          }}>
            <X size={18} />
          </button>
        </div>

        <NavItems collapsed={false} onNavigate={closeMobile} />

        {/* User */}
        <div style={{ padding: '12px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--card)', borderRadius: 10, padding: '10px'
          }}>
            <UserAvatar name={user?.name} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 500, color: 'var(--text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}>
                {user?.name || 'Admin'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{user?.role}</div>
            </div>
            <button onClick={() => { logout(); closeMobile(); }} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text3)', padding: 4, borderRadius: 5, display: 'flex',
              transition: 'color 0.15s'
            }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--red)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}>
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Shared atoms */
function LogoMark() {
  return (
    <div
      style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, color: '#fff', fontFamily: "'Playfair Display', serif",
        fontStyle: 'italic', fontWeight: 400
      }}>
      <img src={aural_logo} alt="Aurel Global" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </div>
  );
}

function UserAvatar({ name }: { name?: string }) {
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'A';
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, var(--or), var(--or2))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, color: '#fff'
    }}>
      {initials}
    </div>
  );
}

/* Main export */
export default function Sidebar() {
  return (
    <>
      <div className="sidebar-desktop"><DesktopSidebar /></div>
      <MobileDrawer />
      <style>{`
        .sidebar-desktop { display: flex; }
        @media (max-width: 1023px) { .sidebar-desktop { display: none; } }
      `}</style>
    </>
  );
}
